import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WireChatMessage } from '@logistics/contracts';
import type { Me } from '../auth/use-me';
import { runTurn } from './run-turn';
import type { Ask, LiveTurn } from './live-turn';
import { conversationKey } from './use-chat-data';

/**
 * One question at a time: `send` starts it, `abort` stops it (on leaving the
 * conversation, or the screen). The thread hides the live turn once the cache
 * holds its messages, so nothing on screen jumps or shows twice.
 */
export function useChatTurn(
  me: Me,
  activeId: string | null,
  setActiveId: (id: string) => void,
  onAnswered: (message: WireChatMessage) => void,
): { turn: LiveTurn | null; send: (ask: Ask) => void; abort: () => void } {
  const client = useQueryClient();
  const [turn, setTurn] = useState<LiveTurn | null>(null);
  const controller = useRef<AbortController | null>(null);
  const counter = useRef(0);
  const turnRef = useRef<LiveTurn | null>(null);

  useEffect(() => {
    turnRef.current = turn;
  }, [turn]);
  useEffect(() => () => controller.current?.abort(), []);

  /** Stops the answer in flight; the server may have kept the question, so re-read it. */
  const abort = useCallback(() => {
    const current = controller.current;
    controller.current = null;
    const id = turnRef.current?.conversationId;
    if (current !== null && id !== undefined) {
      void client.invalidateQueries({ queryKey: conversationKey(id) });
    }
    current?.abort();
    setTurn(null);
  }, [client]);

  const send = useCallback(
    (ask: Ask) => {
      if (controller.current !== null) return;
      const ctl = new AbortController();
      controller.current = ctl;
      counter.current += 1;
      const key = counter.current;
      void runTurn(ask, {
        client,
        me,
        activeId,
        signal: ctl.signal,
        key,
        start: setTurn,
        update: (patch) => setTurn((t) => (t?.key === key ? { ...t, ...patch } : t)),
        setActiveId,
        onAnswered,
      }).finally(() => {
        if (controller.current === ctl) controller.current = null;
      });
    },
    [activeId, client, me, onAnswered, setActiveId],
  );

  return { turn, send, abort };
}
